import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowRight, ChevronRight } from "lucide-react";
import Seo, { buildBreadcrumbLd, buildFaqLd } from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import PageHero from "../components/PageHero";
import Reveal, { StaggerGroup, itemVariants } from "../components/Reveal";
import FaqAccordion from "../components/FaqAccordion";
import CtaSection from "../components/CtaSection";
import Icon from "../components/Icon";
import { motion } from "framer-motion";
import { getService, services } from "../data/services";
import vanBrandedImg from "../assets/rt-van-branded.webp";

const serviceImages = {
  van: { src: vanBrandedImg, alt: "Rümpeltaxi Transporter gelb-schwarz, Entrümpelung Ulm Neu-Ulm Altenstadt" },
};

function Block({ title, items, dark }) {
  return (
    <div>
      <h2 className={`font-heading text-2xl font-bold ${dark ? "text-white" : "text-anthracite"}`}>{title}</h2>
      <StaggerGroup className="mt-6 space-y-3">
        {items.map((it) => (
          <motion.div key={it} variants={itemVariants} className="flex items-start gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-yellow text-brand-black">
              <Check className="h-4 w-4" />
            </span>
            <span className={`text-base leading-relaxed ${dark ? "text-light-gray/80" : "text-mid-gray"}`}>{it}</span>
          </motion.div>
        ))}
      </StaggerGroup>
    </div>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getService(slug);
  if (!service) return <Navigate to="/leistungen" replace />;

  const crumbs = [
    { label: "Startseite", to: "/" },
    { label: "Leistungen", to: "/leistungen" },
    { label: service.title, to: `/leistungen/${service.slug}` },
  ];

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    serviceType: service.title,
    provider: { "@type": "ProfessionalService", name: "Rümpeltaxi" },
    areaServed: ["Ulm", "Neu-Ulm", "Stuttgart", "Memmingen"],
    description: service.metaDescription,
  };

  const related = service.related.map(getService).filter(Boolean);

  return (
    <div data-testid={`page-service-${service.slug}`}>
      <Seo
        path={`/leistungen/${service.slug}`}
        title={service.metaTitle}
        description={service.metaDescription}
        jsonLd={[buildBreadcrumbLd(crumbs), serviceLd, buildFaqLd(service.faq)]}
      />
      <Breadcrumbs items={crumbs} />
      <PageHero kicker="Leistung" title={service.h1} lead={service.intro}>
        <div className="mt-8">
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 rounded-sm bg-yellow px-6 py-3.5 font-heading font-bold text-brand-black transition-colors hover:bg-yellow-dark"
            data-testid="service-cta-top"
          >
            Angebot für {service.title} anfragen <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </PageHero>

      <section className="bg-white py-16 md:py-24">
        <div className="container-page">
          {service.body?.length > 0 && (
            <Reveal className="mb-14 max-w-3xl space-y-4" data-testid="service-body">
              {service.body.map((p, i) => (
                <p key={i} className="text-base leading-relaxed text-mid-gray">
                  {p}
                </p>
              ))}
            </Reveal>
          )}
          {serviceImages[service.image] && (
            <Reveal className="mb-14">
              <figure className="overflow-hidden rounded-md border-4 border-anthracite">
                <img
                  src={serviceImages[service.image].src}
                  alt={serviceImages[service.image].alt}
                  className="w-full object-cover"
                  loading="lazy"
                  decoding="async"
                  data-testid="service-image"
                />
              </figure>
            </Reveal>
          )}
          <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <span className="flex h-14 w-14 items-center justify-center rounded-sm bg-yellow text-brand-black">
              <Icon name={service.icon} className="h-7 w-7" />
            </span>
            <Block title="Typische Einsatzsituationen" items={service.situations} />
          </Reveal>
          <Reveal delay={1}>
            <Block title="Leistungsumfang" items={service.scope} />
          </Reveal>
          </div>
        </div>
      </section>

      {/* Ablauf */}
      <section className="bg-light-gray py-16 md:py-24">
        <div className="container-page">
          <Reveal>
            <h2 className="font-heading text-2xl font-bold text-anthracite md:text-3xl">So gehen wir vor</h2>
          </Reveal>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {service.process.map((step, i) => (
              <Reveal key={i} delay={i * 0.4}>
                <div className="h-full rounded-sm border border-white bg-white p-6">
                  <span className="font-heading text-3xl font-extrabold text-yellow">{String(i + 1).padStart(2, "0")}</span>
                  <p className="mt-3 text-sm leading-relaxed text-mid-gray">{step}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Extras + Kostenfaktoren */}
      <section className="bg-anthracite py-16 text-white md:py-24">
        <div className="container-page grid gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <Block title="Mögliche Zusatzleistungen" items={service.extras} dark />
          </Reveal>
          <Reveal delay={1}>
            <Block title="Diese Faktoren beeinflussen die Kosten" items={service.costFactors} dark />
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-16 md:py-24">
        <div className="container-page grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
          <Reveal>
            <span className="kicker">FAQ</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-anthracite md:text-4xl">
              Fragen zu {service.title}
            </h2>
          </Reveal>
          <Reveal delay={1}>
            <FaqAccordion items={service.faq} idPrefix={service.slug} />
          </Reveal>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-light-gray py-16 md:py-20">
          <div className="container-page">
            <h2 className="font-heading text-xl font-bold text-anthracite">Passende Leistungen</h2>
            <div className="mt-6 flex flex-wrap gap-3">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/leistungen/${r.slug}`}
                  className="inline-flex items-center gap-1 rounded-sm border border-mid-gray/20 bg-white px-4 py-2.5 font-heading text-sm font-semibold text-anthracite transition-colors hover:border-yellow"
                  data-testid={`related-${r.slug}`}
                >
                  {r.title} <ChevronRight className="h-4 w-4 text-mid-gray" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CtaSection title={`Bereit für Ihre ${service.title}?`} />
    </div>
  );
}
